<script lang="ts">
	/**
	 * ExhibitPaQueue — prior auth queue with status + cycle-time collapse.
	 *
	 * Beat 01: Prior authorization, end-to-end.
	 * Pain anchor: 13 hrs / MD / week on PA work (AMA 2024).
	 *
	 * Two-part exhibit:
	 *   Top — small queue of 4 PAs with current status (drafted /
	 *         submitted / approved / needs you), Ekavi-mark on the
	 *         drafted ones.
	 *   Bottom — side-by-side "before / after" timeline showing the
	 *           industry 8-day PA cycle compressing to ~36 hrs on Ekavi.
	 *
	 * Pure HTML/CSS, static. No animation.
	 */

	import EkaviMark from '../EkaviMark.svelte';

	interface Row {
		patient: string;
		desc: string;
		status: 'drafted' | 'submitted' | 'approved' | 'needs';
		statusLabel: string;
		statusTime: string;
		drafted?: boolean;
	}

	const ROWS: ReadonlyArray<Row> = [
		{
			patient: 'Hernandez',
			desc: 'UHC · MRI lumbar',
			status: 'approved',
			statusLabel: 'approved',
			statusTime: '36h',
			drafted: true
		},
		{
			patient: 'Wong',
			desc: 'BCBS · arthroscopy',
			status: 'submitted',
			statusLabel: 'submitted',
			statusTime: '4h',
			drafted: true
		},
		{
			patient: 'Adeyemi',
			desc: 'Aetna · genetic panel',
			status: 'drafted',
			statusLabel: 'drafted',
			statusTime: 'now',
			drafted: true
		},
		{
			patient: 'Patel',
			desc: 'Humana · oncology infusion',
			status: 'needs',
			statusLabel: 'needs you',
			statusTime: '12m'
		}
	];
</script>

<figure
	class="exhibit"
	aria-label="Sample prior authorization queue showing four cases at different stages and what Ekavi handles in the workflow"
>
	<div class="head">
		<span class="eyebrow">Prior auths · this morning</span>
		<span class="count">4 active</span>
	</div>

	<ul class="rows">
		{#each ROWS as row (row.patient)}
			<li class="row" data-status={row.status}>
				<span class="lead" aria-hidden="true">
					{#if row.drafted}
						<EkaviMark size={14} decorative maskIdSuffix={`pa-${row.patient}`} />
					{:else}
						<span class="dot dot-ochre"></span>
					{/if}
				</span>
				<span class="patient">{row.patient}</span>
				<span class="desc">{row.desc}</span>
				<span class="status">{row.statusLabel}</span>
				<span class="when">{row.statusTime}</span>
			</li>
		{/each}
	</ul>

	<div class="steps">
		<p class="steps-eyebrow">What Ekavi handles</p>
		<ul class="steps-list">
			<li class="step">
				<span class="step-num">1</span>
				<span class="step-text">Pulls clinical context from the chart and drafts the request</span>
			</li>
			<li class="step">
				<span class="step-num">2</span>
				<span class="step-text">Submits to the payer once a clinician approves</span>
			</li>
			<li class="step">
				<span class="step-num">3</span>
				<span class="step-text">Tracks status and surfaces missing docs before they cause a denial</span>
			</li>
			<li class="step step-note">
				<span class="step-num">·</span>
				<span class="step-text">Clinical decisions always pause for sign-off</span>
			</li>
		</ul>
	</div>
</figure>

<style>
	.exhibit {
		background: var(--ekavi-paper);
		border: var(--ekavi-stroke-hairline);
		padding: var(--ekavi-space-6);
		margin: 0;
	}
	.head {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		padding-bottom: var(--ekavi-space-3);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.eyebrow {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
	}
	.count {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		font-variant-numeric: tabular-nums;
	}
	.rows {
		list-style: none;
		padding: 0;
		margin: 0 0 var(--ekavi-space-6);
	}
	.row {
		display: grid;
		grid-template-columns: 24px minmax(80px, 1fr) minmax(0, 1.6fr) auto auto;
		align-items: center;
		gap: var(--ekavi-space-3);
		padding-block: var(--ekavi-space-2);
		border-bottom: var(--ekavi-stroke-hairline);
		min-height: var(--ekavi-row-triage);
	}
	.row:last-child {
		border-bottom: none;
	}
	.lead {
		display: inline-flex;
		justify-content: center;
		align-items: center;
		color: var(--ekavi-charcoal);
	}
	.dot {
		width: 6px;
		height: 6px;
		border-radius: 999px;
		display: inline-block;
	}
	.dot-ochre {
		background: var(--ekavi-ochre);
	}
	.patient {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-halbfett);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
	}
	.desc {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-2);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.status {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-2);
		text-transform: lowercase;
	}
	.row[data-status='approved'] .status {
		color: var(--ekavi-sage);
	}
	.row[data-status='needs'] .status {
		color: var(--ekavi-ochre);
		font-weight: var(--ekavi-weight-halbfett);
	}
	.when {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		font-variant-numeric: tabular-nums;
		text-align: right;
	}

	.steps {
		padding-top: var(--ekavi-space-4);
		border-top: var(--ekavi-stroke-hairline);
	}
	.steps-eyebrow {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
		margin: 0 0 var(--ekavi-space-3);
	}
	.steps-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: var(--ekavi-space-2);
	}
	.step {
		display: grid;
		grid-template-columns: 16px 1fr;
		gap: var(--ekavi-space-3);
		align-items: baseline;
	}
	.step-num {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		text-align: center;
	}
	.step-text {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-2);
	}
	.step-note .step-text {
		color: var(--ekavi-ink-3);
		font-style: italic;
	}

	@media (max-width: 480px) {
		.row {
			grid-template-columns: 20px minmax(70px, 1fr) auto auto;
		}
		.desc {
			grid-column: 2 / -1;
			grid-row: 2;
			margin-top: 2px;
		}
	}
</style>
